import React, { useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Fileira horizontal estilo Netflix: título à esquerda, "Ver todos" à
 * direita, scroll horizontal por toque e setas laterais no hover (desktop).
 * A classe .academia-hscroll (esconde a scrollbar) é definida uma vez no
 * <style> do AcademiaHome.
 */
const NetflixRow = ({ title, count, onVerTodos, verTodosLabel, children, isMobile, prevLabel = 'Anterior', nextLabel = 'Próximo', onMoveUp, onMoveDown, moveUpLabel, moveDownLabel, variant = 'default' }) => {
  const isSub = variant === 'sub';
  const scrollRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  // Arrastar-pra-rolar com o MOUSE (igual ao carrossel da Dobra de Nicho). Só pra
  // mouse — touch/trackpad seguem com o scroll nativo (não dá pra brigar com o
  // momentum). Um drag de verdade (>4px) cancela o clique no card (não abre à toa).
  const dragRef = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const onDragDown = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
  };
  const onDragMove = (e) => {
    const el = scrollRef.current;
    if (!el || !dragRef.current.down) return;
    const dx = e.clientX - dragRef.current.startX;
    if (Math.abs(dx) > 4) dragRef.current.moved = true;
    el.scrollLeft = dragRef.current.startScroll - dx;
  };
  const onDragEnd = () => { dragRef.current.down = false; };

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const Arrow = ({ dir, visible }) => (
    <button
      onClick={() => scrollBy(dir)}
      aria-label={dir < 0 ? prevLabel : nextLabel}
      style={{
        position: 'absolute', top: 0, bottom: 0,
        [dir < 0 ? 'left' : 'right']: 0,
        width: '44px', border: 'none', cursor: 'pointer', zIndex: 5,
        background: `linear-gradient(${dir < 0 ? '270deg' : '90deg'}, transparent, rgba(10,10,10,0.82))`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.2s ease',
        color: '#F5F5F5',
      }}
    >
      {dir < 0 ? <ChevronLeft size={26} /> : <ChevronRight size={26} />}
    </button>
  );

  return (
    <div style={{ marginBottom: isSub ? '18px' : '34px' }}>
      {/* Cabeçalho da fileira */}
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: isSub ? '9px' : '12px', paddingRight: '4px',
      }}>
        <h2 style={{
          margin: 0, display: 'inline-flex', alignItems: 'center', gap: '8px',
          fontSize: isSub ? '13.5px' : '16px', fontWeight: isSub ? 600 : 700,
          letterSpacing: '-0.2px',
          color: isSub ? 'var(--dp-text-secondary)' : 'var(--dp-text-primary)',
          fontFamily: 'var(--dp-font)',
        }}>
          {isSub && (
            <span style={{
              width: '5px', height: '5px', borderRadius: '50%', flexShrink: 0,
              background: 'var(--dp-blue-500)', display: 'inline-block',
            }} />
          )}
          {title}
          {typeof count === 'number' && count > 0 && (
            <span style={{
              marginLeft: '2px', fontSize: '12px', fontWeight: 500,
              color: 'var(--dp-text-tertiary)',
            }}>
              {count}
            </span>
          )}
        </h2>
        {/* Reordenação de seções (super admin) */}
        {(onMoveUp || onMoveDown) && (
          <div style={{ display: 'flex', gap: '2px', marginLeft: '10px', marginRight: 'auto' }}>
            <button
              onClick={onMoveUp || undefined}
              disabled={!onMoveUp}
              title={moveUpLabel || 'Mover seção para cima'}
              style={{
                background: 'none', border: '1px solid var(--dp-border-subtle)', borderRadius: '6px',
                padding: '3px', cursor: onMoveUp ? 'pointer' : 'default', display: 'flex',
                color: onMoveUp ? 'var(--dp-text-secondary)' : 'var(--dp-border-medium)',
                opacity: onMoveUp ? 1 : 0.4,
              }}
            >
              <ChevronUp size={12} />
            </button>
            <button
              onClick={onMoveDown || undefined}
              disabled={!onMoveDown}
              title={moveDownLabel || 'Mover seção para baixo'}
              style={{
                background: 'none', border: '1px solid var(--dp-border-subtle)', borderRadius: '6px',
                padding: '3px', cursor: onMoveDown ? 'pointer' : 'default', display: 'flex',
                color: onMoveDown ? 'var(--dp-text-secondary)' : 'var(--dp-border-medium)',
                opacity: onMoveDown ? 1 : 0.4,
              }}
            >
              <ChevronDown size={12} />
            </button>
          </div>
        )}
        {onVerTodos && (
          <button
            onClick={onVerTodos}
            style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              color: 'var(--dp-blue-500)', fontSize: '12px', fontWeight: 600,
              fontFamily: 'var(--dp-font)',
            }}
          >
            {verTodosLabel || 'Ver todos'} <ChevronRight size={13} />
          </button>
        )}
      </div>

      {/* Trilho com setas */}
      <div
        style={{ position: 'relative' }}
        onMouseEnter={() => { setHover(true); updateArrows(); }}
        onMouseLeave={() => setHover(false)}
      >
        {!isMobile && <Arrow dir={-1} visible={hover && canLeft} />}
        {!isMobile && <Arrow dir={1} visible={hover && canRight} />}
        <div
          ref={scrollRef}
          className="academia-hscroll"
          onScroll={updateArrows}
          onPointerDown={onDragDown}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerLeave={onDragEnd}
          onClickCapture={(e) => { if (dragRef.current.moved) { e.stopPropagation(); e.preventDefault(); dragRef.current.moved = false; } }}
          style={{
            display: 'flex', gap: '12px',
            overflowX: 'auto',
            // Respiro para o scale(1.045) do hover não ser clipado (incl. lateral)
            padding: '6px 8px 10px 8px',
            margin: '0 -8px',
            scrollSnapType: 'x proximity',
            WebkitOverflowScrolling: 'touch',
            cursor: 'grab',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default NetflixRow;
